import React from "react";
import AsyncSelect from "react-select/async";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { RecipeUnit } from "./productRecipeApi";
import {
  DraftRow,
  IngredientOption,
  defaultUnit,
  optionFor,
  unitsFor,
} from "./productRecipeEditor";
import RecipeFieldError, {
  RecipeErrors,
  recipeFieldProps,
} from "./RecipeFieldError";
import { control, ingredientSelectStyles } from "./recipeStyles";
interface Props {
  row: DraftRow;
  index: number;
  rows: DraftRow[];
  disabled: boolean;
  canManage: boolean;
  errors: RecipeErrors;
  search: (input: string) => Promise<IngredientOption[]>;
  changeRow: (key: number, values: Partial<DraftRow>) => void;
  onRemove: (key: number) => void;
}
function ProductLabel({ option }: { option: IngredientOption }) {
  return (
    <div className="min-w-0 leading-tight">
      <span className="block truncate text-sm font-medium">
        {option.product.name}
      </span>
      <span className="mt-0.5 block text-xs text-slate-500">
        PLU {option.product.plu}
      </span>
    </div>
  );
}
export default function RecipeIngredientRow({
  row,
  index,
  rows,
  disabled,
  canManage,
  errors,
  search,
  changeRow,
  onRemove,
}: Props) {
  const errorFor = (field: string) => (
    <RecipeFieldError field={field} errors={errors} />
  );
  const propsFor = (field: string) => recipeFieldProps(field, errors);
  return (
    <tr
      className="grid grid-cols-[minmax(0,1fr)_6rem_2.75rem] items-end gap-3 border-b border-slate-100 px-3 py-3 last:border-b-0 hover:bg-slate-50/60 sm:table-row"
      aria-label={`Ingredient ${index + 1}`}
    >
      <td className="col-span-3 min-w-0 sm:table-cell sm:px-4 sm:py-2.5">
        <label htmlFor={`recipe-ingredient-${row.key}`} className="sr-only">
          Ingredient product {index + 1}
        </label>
        <AsyncSelect<IngredientOption>
          inputId={`recipe-ingredient-${row.key}`}
          aria-label={`Ingredient product ${index + 1}`}
          loadOptions={search}
          defaultOptions
          cacheOptions={false}
          value={row.product ? optionFor(row.product) : null}
          isDisabled={disabled}
          styles={ingredientSelectStyles}
          menuPortalTarget={
            typeof document === "undefined" ? undefined : document.body
          }
          menuPosition="fixed"
          formatOptionLabel={(option) => <ProductLabel option={option} />}
          isOptionDisabled={(option) =>
            rows.some(
              (other) =>
                other.key !== row.key && other.product?.id === option.value
            )
          }
          onChange={(option) =>
            changeRow(row.key, {
              product: option?.product ?? null,
              unit: defaultUnit(option?.product.selling_unit_of_measure ?? 1),
              available: true,
            })
          }
          placeholder="Search name or PLU…"
          noOptionsMessage={() =>
            "No active products found. Try a name or PLU."
          }
          {...propsFor(`items.${index}.ingredient_product_id`)}
        />
        {!row.available && (
          <p className="mt-1 text-xs text-red-700">
            Ingredient unavailable. Replace or remove it before saving.
          </p>
        )}
        {errorFor(`items.${index}.ingredient_product_id`)}
      </td>
      <td className="min-w-0 sm:table-cell sm:px-2 sm:py-2.5 sm:align-middle">
        <label htmlFor={`recipe-quantity-${row.key}`} className="sr-only">
          Quantity {index + 1}
        </label>
        <span
          aria-hidden="true"
          className="mb-1 block text-xs font-medium text-slate-500 sm:hidden"
        >
          Quantity
        </span>
        <input
          id={`recipe-quantity-${row.key}`}
          type="number"
          min="0.0001"
          step={row.unit === "pieces" ? "1" : "0.0001"}
          value={row.quantity}
          disabled={disabled}
          onChange={(event) =>
            changeRow(row.key, { quantity: event.target.value })
          }
          className={`${control} text-right tabular-nums`}
          {...propsFor(`items.${index}.quantity`)}
        />
        {errorFor(`items.${index}.quantity`)}
      </td>
      <td className="min-w-0 sm:table-cell sm:px-2 sm:py-2.5 sm:align-middle">
        <label htmlFor={`recipe-unit-${row.key}`} className="sr-only">
          Unit {index + 1}
        </label>
        <span
          aria-hidden="true"
          className="mb-1 block text-xs font-medium text-slate-500 sm:hidden"
        >
          Unit
        </span>
        <select
          id={`recipe-unit-${row.key}`}
          value={row.unit}
          disabled={disabled}
          onChange={(event) =>
            changeRow(row.key, { unit: event.target.value as RecipeUnit })
          }
          className={control}
          {...propsFor(`items.${index}.unit`)}
        >
          {Array.from(
            new Set([
              ...unitsFor(row.product?.selling_unit_of_measure ?? 1),
              row.unit,
            ])
          ).map((unit) => (
            <option key={unit}>{unit}</option>
          ))}
        </select>
        {errorFor(`items.${index}.unit`)}
      </td>
      <td className="sm:table-cell sm:py-2.5 sm:pr-3 sm:text-right sm:align-middle">
        {canManage && (
          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-40 sm:h-9 sm:w-9"
            disabled={disabled}
            onClick={() => onRemove(row.key)}
            aria-label={`Remove ingredient ${index + 1}`}
            title={`Remove ingredient ${index + 1}`}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        )}
      </td>
    </tr>
  );
}
